export interface Project {
  id: string;
  project_name: string;
  slug: string;
  project_type: 'WEBSITE' | 'SYSTEM' | 'APP';
  classification: string;
  short_description: string;
  challenge: string;
  solution: string;
  result: string;
  services: string[];
  capabilities: string[];
  technologies: string[];
  year: string;
  featured_image: string;
  featured: boolean;
  published: boolean;
  display_order: number;
}

export interface Lead {
  id: string;
  full_name: string;
  company: string;
  email: string;
  phone: string;
  service: string;
  budget: string;
  desired_timeline: string;
  project_description: string;
  lead_status: 'NEW' | 'CONSULTATION_BOOKED' | 'CONSULTATION_COMPLETED' | 'QUOTE_SENT' | 'NEGOTIATING' | 'WON' | 'LOST';
  consultation_date: string | null;
  internal_notes: string;
  created_at: string;
  updated_at: string;
}

export interface Consultation {
  id: string;
  lead_id: string;
  full_name: string;
  company: string;
  email: string;
  phone: string;
  service: string;
  consultation_start: string;
  consultation_end: string;
  timezone: string;
  booking_status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW';
  google_event_id: string | null;
  created_at: string;
}

// Backend integration point: replace with Supabase query → supabase.from('projects').select('*').eq('published', true)
export const mockProjects: Project[] = [
{
  id: 'proj-001',
  project_name: "Austin's Auto Dealership",
  slug: 'austins-auto-dealership',
  project_type: 'SYSTEM',
  classification: 'Full-Stack Dealership Platform',
  short_description:
  'A complete digital dealership platform combining a modern customer-facing vehicle catalogue with a private staff inventory and management system — built for a real Georgetown, Guyana auto dealership.',
  challenge:
  "Austin's Auto Dealership operated across two Georgetown locations with no centralised digital system. Customers had no way to browse inventory online, enquire about vehicles, or pre-order. Staff managed stock manually with no shared dashboard, making it difficult to track availability, process enquiries, or coordinate across locations.",
  solution:
  "A full-stack dealership platform was designed and built — a public-facing vehicle catalogue with search and filter by make, model, year, and price range, individual vehicle detail pages, an accessories section, and a pre-order flow. Behind the scenes, a private admin dashboard gives staff full inventory control, enquiry management, Excel import/export, and multi-role access. WhatsApp integration connects customers directly to the team at +592 635 5435.",
  result:
  "Austin's now operates with a professional online presence that reflects both Georgetown locations. Customers can browse fresh stock, pre-order vehicles and save up to 30%, shop accessories with installation available, and reach the team instantly via WhatsApp. The admin system replaced manual spreadsheets with a scalable digital operation.",
  services: ['Website Design & Development', 'Business Management System', 'Admin Portal', 'Database Architecture'],
  capabilities: [
  'Customer-Facing Website',
  'Vehicle Catalogue & Search',
  'Vehicle Detail Pages',
  'Accessories Section',
  'Pre-Order Flow',
  'WhatsApp Integration',
  'Admin Dashboard',
  'Inventory Management',
  'Staff Login & Access Control',
  'Customer Enquiry System',
  'Excel Import/Export',
  'Responsive Design'],

  technologies: ['Next.js', 'TypeScript', 'Supabase', 'PostgreSQL', 'Tailwind CSS', 'Vercel'],
  year: '2026',
  featured_image: "/assets/images/Screenshot_2026-09-11_163157-1789158873621.png",
  featured: true,
  published: true,
  display_order: 1
},
{
  id: 'proj-002',
  project_name: 'Firmus Trading',
  slug: 'firmus-trading',
  project_type: 'WEBSITE',
  classification: 'Business Website',
  short_description:
  "A responsive business website built for Firmus Trading — a Georgetown-based construction equipment supplier with two locations. Showcases their full product range and routes customer enquiries via WhatsApp.",
  challenge:
  "Firmus Trading had no digital presence to match their growing product range across two Georgetown locations. Contractors and distributors searching online had no way to browse products, check categories, or make enquiries — meaning Firmus was missing inbound business from customers who expected to find them online.",
  solution:
  "A modern responsive business website was designed and built around Firmus Trading's brand identity and five product categories: Construction Equipment, Tools & Accessories, WPC Wall Panels, Scaffolding & Formwork, and SPC Floor Panels. The site features a full product catalogue, category pages, product detail views, a WhatsApp-integrated enquiry flow, and a locations section covering both the Garnett Street and Vergenoegen branches.",
  result:
  "Firmus Trading now has a credible online presence that matches the scale of their operation. Contractors and distributors can browse the full product catalogue, find the nearest location, and send enquiries directly via WhatsApp — giving the business a professional digital channel that works on any device.",
  services: ['Website Design & Development', 'Product Catalogue', 'WhatsApp Integration', 'Brand Identity'],
  capabilities: [
  'Responsive Website',
  'Product Catalogue',
  'Category Pages',
  'Product Detail Pages',
  'WhatsApp Enquiries',
  'Locations Section',
  'Custom Brand Design',
  'Scroll Animations',
  'Mobile Optimised'],

  technologies: ['Next.js', 'TypeScript', 'Tailwind CSS', 'Vercel'],
  year: '2026',
  featured_image: '/assets/images/Screenshot_2026-09-11_164311-1789160979476.png',
  featured: true,
  published: true,
  display_order: 2
},
{
  id: 'proj-003',
  project_name: 'Ride Go',
  slug: 'tuki',
  project_type: 'APP',
  classification: 'Passenger + Driver App System',
  short_description:
  'A transportation platform designed around passenger booking, driver operations, live ride management and the complete trip lifecycle.',
  challenge:
  'Local transportation services operated without a structured digital system — drivers relied on phone calls, passengers had no reliable booking method, and there was no central view of active trips, driver availability, or payment status.',
  solution:
  'A two-sided transportation platform was designed — a passenger app for booking rides and tracking trips, a driver app for accepting assignments and managing their schedule, and an operational backend for monitoring active rides, managing drivers, and processing payments.',
  result:
  'Ride Go provides a complete digital infrastructure for a transportation service — from passenger booking to driver assignment, live tracking, payment processing, and operational oversight — replacing manual coordination with a structured platform.',
  services: ['Mobile App Design', 'Web Application', 'System Architecture', 'Database Design'],
  capabilities: [
  'Passenger App',
  'Driver App',
  'Ride Booking',
  'Driver Assignment',
  'Live Map Tracking',
  'Trip Management',
  'Payment Processing',
  'Scheduling',
  'Database Architecture',
  'Admin/Operational Logic'],

  technologies: ['React Native', 'Next.js', 'Supabase', 'PostgreSQL', 'Maps API', 'Stripe'],
  year: '2025',
  featured_image: '/assets/images/ChatGPT_Image_Sep_15__2026__12_41_50_PM-1789490691390.png',
  featured: true,
  published: true,
  display_order: 3
}];



// Backend integration point: replace with Supabase query → supabase.from('leads').select('*').order('created_at', { ascending: false })
export const mockLeads: Lead[] = [
{
  id: 'lead-001',
  full_name: 'Marcus Thompson',
  company: 'Thompson Hardware Ltd',
  email: 'marcus@thompsonhardware.com',
  phone: '+592 671 2234',
  service: 'Business Management System',
  budget: 'GYD 250,000 – 500,000',
  desired_timeline: '1–2 Months',
  project_description: 'Need an inventory and staff management system for our hardware store. Currently using spreadsheets and losing track of stock levels.',
  lead_status: 'NEW',
  consultation_date: null,
  internal_notes: '',
  created_at: '2026-09-09T10:22:00Z',
  updated_at: '2026-09-09T10:22:00Z'
},
{
  id: 'lead-002',
  full_name: 'Priya Ramkhelawan',
  company: 'Bloom Boutique',
  email: 'priya@bloomboutique.gy',
  phone: '+592 655 8821',
  service: 'Website',
  budget: 'GYD 250,000 – 500,000',
  desired_timeline: 'ASAP',
  project_description: 'Fashion boutique needs a modern website with product catalogue and WhatsApp ordering.',
  lead_status: 'CONSULTATION_BOOKED',
  consultation_date: '2026-09-11T14:00:00Z',
  internal_notes: 'Very clear on what she wants. Budget confirmed. Priority client.',
  created_at: '2026-09-07T14:15:00Z',
  updated_at: '2026-09-08T09:30:00Z'
},
{
  id: 'lead-003',
  full_name: 'Devon Charles',
  company: 'Charles Construction',
  email: 'devon@charlesconstruction.gy',
  phone: '+592 682 4491',
  service: 'Website',
  budget: 'GYD 500,000 – 1,000,000',
  desired_timeline: '3–6 Months',
  project_description: 'Construction company website with project portfolio, services, and enquiry form.',
  lead_status: 'QUOTE_SENT',
  consultation_date: '2026-09-03T10:00:00Z',
  internal_notes: 'Sent quote on Sept 5. Waiting on board approval. Follow up Sept 12.',
  created_at: '2026-09-01T11:00:00Z',
  updated_at: '2026-09-05T16:00:00Z'
},
{
  id: 'lead-004',
  full_name: 'Anika Persaud',
  company: 'AP Medical Supplies',
  email: 'anika@apmedical.gy',
  phone: '+592 643 7712',
  service: 'Business Management System',
  budget: 'GYD 500,000 – 1,000,000',
  desired_timeline: '1–2 Months',
  project_description: 'Medical supply company needs inventory management, order tracking, and customer portal.',
  lead_status: 'NEGOTIATING',
  consultation_date: '2026-08-28T15:00:00Z',
  internal_notes: 'Negotiating on scope. They want payment integration but budget is tight. Proposed phased approach.',
  created_at: '2026-08-25T09:00:00Z',
  updated_at: '2026-09-06T13:00:00Z'
},
{
  id: 'lead-005',
  full_name: 'Roland Singh',
  company: 'Singh Auto Parts',
  email: 'roland@singhautoparts.com',
  phone: '+592 628 3345',
  service: 'Website',
  budget: 'GYD 250,000 – 500,000',
  desired_timeline: 'ASAP',
  project_description: 'Auto parts retailer needs website with parts catalogue and online enquiry.',
  lead_status: 'WON',
  consultation_date: '2026-08-15T10:00:00Z',
  internal_notes: 'Contract signed Aug 20. Project started. Deposit received.',
  created_at: '2026-08-10T14:00:00Z',
  updated_at: '2026-08-20T10:00:00Z'
},
{
  id: 'lead-006',
  full_name: 'Natasha Williams',
  company: 'NW Events & Catering',
  email: 'natasha@nwevents.gy',
  phone: '+592 661 9982',
  service: 'App / Web Application',
  budget: 'GYD 1,000,000+',
  desired_timeline: '3–6 Months',
  project_description: 'Event booking platform with vendor management, client bookings, and payment processing.',
  lead_status: 'CONSULTATION_COMPLETED',
  consultation_date: '2026-09-05T11:00:00Z',
  internal_notes: 'Great call. Scope is large. Preparing detailed quote.',
  created_at: '2026-09-02T16:00:00Z',
  updated_at: '2026-09-05T12:00:00Z'
},
{
  id: 'lead-007',
  full_name: 'Kelvin Boodoo',
  company: 'Boodoo Pharmacy',
  email: 'kelvin@boodoopharmacy.gy',
  phone: '+592 674 5566',
  service: 'Business Management System',
  budget: 'GYD 250,000 – 500,000',
  desired_timeline: '1–2 Months',
  project_description: 'Pharmacy inventory system with expiry tracking, reorder alerts, and staff access.',
  lead_status: 'NEW',
  consultation_date: null,
  internal_notes: '',
  created_at: '2026-09-08T08:45:00Z',
  updated_at: '2026-09-08T08:45:00Z'
},
{
  id: 'lead-008',
  full_name: 'Simone Rodrigues',
  company: 'SR Fitness Studio',
  email: 'simone@srfitness.gy',
  phone: '+592 659 1123',
  service: 'Website',
  budget: 'Under GYD 250,000',
  desired_timeline: 'ASAP',
  project_description: 'Fitness studio website with class schedule, online booking, and membership info.',
  lead_status: 'LOST',
  consultation_date: '2026-08-20T14:00:00Z',
  internal_notes: 'Client decided to use a template builder instead. Budget too low for custom work.',
  created_at: '2026-08-18T10:00:00Z',
  updated_at: '2026-08-22T09:00:00Z'
}];


// Backend integration point: replace with Supabase query → supabase.from('consultations').select('*').order('consultation_start', { ascending: true })
export const mockConsultations: Consultation[] = [
{
  id: 'cons-001',
  lead_id: 'lead-002',
  full_name: 'Priya Ramkhelawan',
  company: 'Bloom Boutique',
  email: 'priya@bloomboutique.gy',
  phone: '+592 655 8821',
  service: 'Website',
  consultation_start: '2026-09-11T14:00:00Z',
  consultation_end: '2026-09-11T15:00:00Z',
  timezone: 'America/Guyana',
  booking_status: 'CONFIRMED',
  google_event_id: null,
  created_at: '2026-09-08T09:30:00Z'
},
{
  id: 'cons-002',
  lead_id: 'lead-007',
  full_name: 'Kelvin Boodoo',
  company: 'Boodoo Pharmacy',
  email: 'kelvin@boodoopharmacy.gy',
  phone: '+592 674 5566',
  service: 'Business Management System',
  consultation_start: '2026-09-12T10:00:00Z',
  consultation_end: '2026-09-12T11:00:00Z',
  timezone: 'America/Guyana',
  booking_status: 'PENDING',
  google_event_id: null,
  created_at: '2026-09-09T08:50:00Z'
},
{
  id: 'cons-003',
  lead_id: 'lead-001',
  full_name: 'Marcus Thompson',
  company: 'Thompson Hardware Ltd',
  email: 'marcus@thompsonhardware.com',
  phone: '+592 671 2234',
  service: 'Business Management System',
  consultation_start: '2026-09-15T13:00:00Z',
  consultation_end: '2026-09-15T14:00:00Z',
  timezone: 'America/Guyana',
  booking_status: 'CONFIRMED',
  google_event_id: null,
  created_at: '2026-09-09T10:30:00Z'
}];